import { useCallback } from 'react';
import { useAppContext } from '../contexts/AppContext';
import { usePlatform, useDownloadService } from '../contexts/PlatformContext';
import { writeExifData } from '../utils/exifUtils';
import { detectImageFormat, getMimeTypeForFormat } from '../utils/imageFormat';

export function usePersistence() {
  const { files, setFiles, selectedIndices } = useAppContext();
  const platform = usePlatform();
  const downloadService = useDownloadService();

  const persistFiles = useCallback(async (indicesToPersist = null) => {
    const indices = indicesToPersist || (selectedIndices.size > 0
      ? Array.from(selectedIndices)
      : files.map((_, i) => i));

    if (indices.length === 0) return;

    const filesToSave = await Promise.all(indices.map(async (idx) => {
      const file = files[idx];
      // Apply changes to the original buffer
      const format = detectImageFormat({
        fileName: file.name,
        mimeType: file.file?.type,
        buffer: new Uint8Array(file.arrayBuffer),
      });
      const mimeType = file.file?.type || getMimeTypeForFormat(format);
      const newBuffer = await writeExifData(file.arrayBuffer, file.modifiedExifData, {
        fileName: file.name,
        mimeType,
      });
      return {
        ...file,
        format,
        mimeType,
        newBuffer,
        originalIndex: idx
      };
    }));

    if (platform.name === 'electron') {
      // In Electron, mutate files directly (no dialog)
      for (const fileItem of filesToSave) {
        if (fileItem.path) {
          try {
            await platform.file.writeFile(fileItem.newBuffer, fileItem.name, {
              showSaveDialog: false,
              defaultPath: fileItem.path
            });
            console.log(`[Persistence] Saved ${fileItem.name} to ${fileItem.path}`);
          } catch (error) {
            console.error(`[Persistence] Failed to save ${fileItem.name}:`, error);
          }
        } else {
          // Fallback if no path (shouldn't happen in Electron unless it was a blob)
          await downloadService.downloadSingle({
            name: fileItem.name,
            buffer: fileItem.newBuffer,
            mimeType: fileItem.mimeType
          });
        }
      }

      // Update local state to reflect that items are no longer "hasChanges"
      // and update the original arrayBuffer with the new one
      // Also create a fresh File object and thumbnailUrl to prevent stale blob URL errors
      setFiles(prev => prev.map((f, i) => {
        const saved = filesToSave.find(s => s.originalIndex === i);
        if (saved) {
          // Revoke old thumbnail URL to prevent memory leak
          if (f.thumbnailUrl) {
            URL.revokeObjectURL(f.thumbnailUrl);
          }
          // Create a new File object from the updated buffer
          const newFile = new File(
            [saved.newBuffer],
            f.name,
            { type: saved.mimeType, lastModified: Date.now() }
          );
          // Create a new thumbnail URL from the new File
          const newThumbnailUrl = URL.createObjectURL(newFile);
          return {
            ...f,
            file: newFile,
            arrayBuffer: saved.newBuffer,
            thumbnailUrl: newThumbnailUrl,
            exifData: { ...f.modifiedExifData },
            hasChanges: false
          };
        }
        return f;
      }));
    } else {
      // Web platform - use download service
      if (filesToSave.length === 1) {
        const file = filesToSave[0];
        await downloadService.downloadSingle({
          name: file.name,
          buffer: file.newBuffer,
          mimeType: file.mimeType
        });
      } else {
        const data = filesToSave.map(f => ({
          name: f.name,
          buffer: f.newBuffer,
          mimeType: f.mimeType
        }));
        await downloadService.downloadMultiple(data);
      }
    }
  }, [files, selectedIndices, platform, downloadService, setFiles]);

  return { persistFiles };
}
